import type {
    PeriodStatementRow,
    StatementBlockKey,
    UnassignedMovement,
    UnsplitFigures,
    VatRateFigures,
} from '../types/dealerFundStatementPeriod.types';
import { UNASSIGNED_LABEL as UNASSIGNED_GROSS_LABEL } from './fundStatementLabels';
import { sumAtScale } from './sumAtScale';

export interface StatementLine {
    key: string;
    label: string;
    amount: string;
    postings: number;
    vat: string;
    net: string;
    vatRates: VatRateFigures[];
    unsplit: UnsplitFigures;
}

export interface StatementBlockTotal {
    gross: string;
    vat: string;
    net: string;
}

export interface VatStatementBlock {
    key: StatementBlockKey;
    split: boolean;
    lines: StatementLine[];
    total: StatementBlockTotal;
    unsplit: UnsplitFigures;
}

export interface StatementMemo {
    label: string;
    amount: string;
    postings: number;
}

export interface StatementLayout {
    blocks: VatStatementBlock[];
    unassigned: StatementMemo | null;
}

export const STATEMENT_BLOCK_ORDER: readonly StatementBlockKey[] = [
    'motorplan_handled',
    'dealer_handled',
    'internal',
    'paid_out',
];

export const isSplitBlock = (key: StatementBlockKey): boolean =>
    key === 'motorplan_handled' || key === 'dealer_handled';

const isZero = (amount: string): boolean => /^-?0+\.0+$/.test(amount);

const toLine = (row: PeriodStatementRow): StatementLine => ({
    key: row.trancode,
    label: row.name ?? row.trancode,
    amount: row.amount,
    postings: row.postings,
    vat: row.vat,
    net: row.net,
    vatRates: row.vatRates,
    unsplit: row.unsplit,
});

const sumUnsplit = (lines: readonly StatementLine[]): UnsplitFigures => ({
    postings: lines.reduce((sum, line) => sum + line.unsplit.postings, 0),
    gross: sumAtScale(lines.map((line) => line.unsplit.gross), 2),
});

const toVatBlock = (key: StatementBlockKey, rows: PeriodStatementRow[]): VatStatementBlock => {
    const lines = rows.filter((row) => row.block === key).map(toLine);
    return {
        key,
        split: isSplitBlock(key),
        lines,
        total: {
            gross: sumAtScale(lines.map((line) => line.amount), 2),
            vat: sumAtScale(lines.map((line) => line.vat), 2),
            net: sumAtScale(lines.map((line) => line.net), 2),
        },
        unsplit: sumUnsplit(lines),
    };
};

export const groupStatementBlocks = (
    rows: PeriodStatementRow[],
    unassigned?: UnassignedMovement | null,
): StatementLayout => ({
    blocks: STATEMENT_BLOCK_ORDER.map((key) => toVatBlock(key, rows)),
    unassigned:
        unassigned && !isZero(unassigned.amount)
            ? { label: UNASSIGNED_GROSS_LABEL, amount: unassigned.amount, postings: unassigned.postings }
            : null,
});
